import { agentModeAtomFamily } from "@/components/ai/chat/state";
import { AgentMode } from "@/components/ai/prompt-input/agent-mode";
import { cn } from "@/components/podkit/lib/cn";
import { useSetAtom } from "jotai";
import { formatClarifyingQuestionsResponse } from "@/components/ai/clarifying-questions-utils";
import { type FC, useState, useEffect, useCallback } from "react";
import { FragmentClarifyingQuestions } from "@/components/ai/FragmentClarifyingQuestions";
import { FragmentNextStepsProposal } from "@/components/ai/FragmentNextStepsProposal";
import type { PendingInteractiveFragment } from "@/components/ai/pending-interactive-utils";

type PendingInteractiveBlockProps = {
    fragment: PendingInteractiveFragment | null;
    onSendMessage?: (message: string) => void;
    environmentId?: string;
    className?: string;
};

export const PendingInteractiveBlock: FC<PendingInteractiveBlockProps> = ({
    fragment,
    onSendMessage,
    environmentId,
    className,
}) => {
    const setAgentMode = useSetAtom(agentModeAtomFamily(environmentId ?? ""));
    const [actionTaken, setActionTaken] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const [unmounted, setUnmounted] = useState(false);

    useEffect(() => {
        setActionTaken(false);
        setDismissing(false);
        setUnmounted(false);
    }, [fragment?.id]);

    // Start exit animation immediately when the last question is answered
    useEffect(() => {
        if (!actionTaken || fragment?.type !== "clarifyingQuestions") return;
        setDismissing(true);
    }, [actionTaken, fragment?.type]);

    const handleAnimationEnd = useCallback(() => {
        if (dismissing) {
            setUnmounted(true);
        }
    }, [dismissing]);

    if (!fragment || unmounted) return null;

    // For non-clarifyingQuestions, keep instant dismiss
    if (actionTaken && fragment.type !== "clarifyingQuestions") return null;

    const handleSendMessage = (message: string) => {
        setActionTaken(true);
        // Eagerly switch to Agent mode so the prompt box updates without waiting for the backend round-trip.
        if (fragment.type === "nextStepsProposal" && environmentId) {
            setAgentMode(AgentMode.Agent);
        }
        onSendMessage?.(message);
    };

    if (fragment.type === "clarifyingQuestions") {
        return (
            <div
                key={fragment.id}
                className={cn(
                    "mx-1 -mb-2 mt-1 w-auto",
                    dismissing
                        ? "duration-[400ms] ease-in-out animate-out fade-out slide-out-to-bottom-4 fill-mode-forwards"
                        : "duration-[400ms] ease-in-out animate-in fade-in slide-in-from-bottom-4",
                    className,
                )}
                onAnimationEnd={handleAnimationEnd}
            >
                <FragmentClarifyingQuestions
                    fragment={fragment}
                    onSubmit={(answers) => {
                        const response = formatClarifyingQuestionsResponse(fragment.questions, answers);
                        handleSendMessage(response);
                    }}
                />
            </div>
        );
    }

    return (
        <div
            key={fragment.id}
            className={cn(
                "mb-1 flex w-full justify-end duration-200 animate-in fade-in slide-in-from-bottom-2",
                className,
            )}
        >
            <FragmentNextStepsProposal fragment={fragment} onActionClick={handleSendMessage} />
        </div>
    );
};
